import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';

const TYPE_ICONS = {
  player_registered:       '👤',
  agent_added_player:      '🤝',
  player_selfie:           '📷',
  pitch_request_received:  '⚽',
  pitch_request_responded: '✅',
  join_request:            '🏃',
  match_rating:            '⭐',
  trial_application:       '📋',
  player_deleted:          '🗑️',
  group_deleted:           '🗑️',
};

const TYPE_PAGES = {
  pitch_request_received:  'pitch-requests',
  pitch_request_responded: 'pitch-requests',
  player_registered:       'players',
  agent_added_player:      'players',
  player_selfie:           'selfie-verification',
  match_rating:            'match-ratings',
  join_request:            'community-groups',
  trial_application:       'trial-applications',
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function NotificationsPanel({ onClose }) {
  const { token } = useApp();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/app/notifications?limit=20', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      setNotifications(d.data || []);
      setUnreadCount(d.unreadCount || 0);
    } catch(e) {}
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await fetch('/api/app/notifications/read', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: [id] })
    }).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await fetch('/api/app/notifications/read', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    }).catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
  };

  const handleClick = async (n) => {
    // Mark as read
    if (!n.isRead) await markRead(n.id);
    
    // Navigate to related page
    const targetPage = TYPE_PAGES[n.type];
    if (targetPage) {
      onClose();
      window.dispatchEvent(new CustomEvent('crm-navigate', { detail: targetPage }));
    }
  };

  const viewAll = () => {
    onClose();
    window.dispatchEvent(new CustomEvent('crm-navigate', { detail: 'notifications' }));
  };

  return (
    <div style={{ position:'fixed', top:60, right:16, width:380, maxHeight:520, background:'#0d1a0d', border:'1px solid rgba(167,244,50,0.25)', borderRadius:14, boxShadow:'0 12px 48px rgba(0,0,0,0.95)', zIndex:9999, display:'flex', flexDirection:'column', overflow:'hidden' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 18px', borderBottom:'1px solid var(--border)' }}>
        <span style={{ fontWeight:700, fontSize:15 }}>
          Notifications
          {unreadCount > 0 && <span style={{ background:'#a7f432', color:'#0f1a10', borderRadius:20, padding:'1px 8px', fontSize:11, marginLeft:8 }}>{unreadCount}</span>}
        </span>
        <div style={{ display:'flex', gap:12, alignItems:'center' }}>
          {unreadCount > 0 && <button onClick={markAllRead} style={{ background:'none', border:'none', color:'#a7f432', cursor:'pointer', fontSize:12, fontWeight:600 }}>Mark all read</button>}
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-dim)', cursor:'pointer', fontSize:18 }}>✕</button>
        </div>
      </div>

      <div style={{ overflowY:'auto', flex:1 }}>
        {loading ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)' }}>Loading...</div>
        ) : notifications.length === 0 ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)' }}>
            <div style={{ fontSize:32, marginBottom:8 }}>🔔</div>
            <div>No notifications yet</div>
          </div>
        ) : notifications.map(n => (
          <div key={n.id}
            onClick={() => handleClick(n)}
            style={{
              display:'flex', gap:12, padding:'12px 18px',
              borderBottom:'1px solid rgba(255,255,255,0.04)',
              cursor: TYPE_PAGES[n.type] ? 'pointer' : 'default',
              background: n.isRead ? 'transparent' : 'rgba(167,244,50,0.04)',
              transition:'background 0.15s',
            }}
            onMouseEnter={e => { if (TYPE_PAGES[n.type]) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = n.isRead ? 'transparent' : 'rgba(167,244,50,0.04)'; }}
          >
            <div style={{ fontSize:20, flexShrink:0, marginTop:2 }}>{TYPE_ICONS[n.type] || '🔔'}</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight: n.isRead ? 500 : 700, fontSize:13, color:'var(--text-primary)', marginBottom:2 }}>{n.title}</div>
              {n.message && <div style={{ fontSize:12, color:'var(--text-dim)', marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{n.message}</div>}
              <div style={{ fontSize:11, color:'var(--text-dim)' }}>{timeAgo(n.createdAt)}</div>
            </div>
            {!n.isRead && <div style={{ width:8, height:8, borderRadius:'50%', background:'#a7f432', flexShrink:0, marginTop:6 }} />}
          </div>
        ))}
      </div>

      <div style={{ padding:'10px 18px', borderTop:'1px solid var(--border)', textAlign:'center' }}>
        <button onClick={viewAll} style={{ background:'none', border:'none', color:'#a7f432', cursor:'pointer', fontSize:13, fontWeight:600 }}>
          View all activity →
        </button>
      </div>
    </div>
  );
}
